import * as React from 'react';
import { IndoorGeofencingMode } from '@ixicare/resources';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import { useZonePresets, useZonePresetMutations } from './useZonePresets';

export interface GeofenceSelection {
  zoneIds: string[];
  // Null means the alarm sender or care group holds this list themselves, following nothing.
  zonePresetId: string | null;
}

const CUSTOM = '';

const sameZones = (left: string[], right: string[]) =>
  left.length === right.length && [...left].sort().join() === [...right].sort().join();

interface Options {
  siteId?: number;
  // Only an alarm sender's list has a polarity. Absent for a group or a preset, both of which only
  // ever name where someone may be.
  mode?: IndoorGeofencingMode;
  initialPresetId: string | null;
  // The zone selection, owned by the caller: picking a preset replaces it, and saving reads it.
  selection: string[];
  setSelection: (zoneIds: string[]) => void;
  onSave: (selection: GeofenceSelection) => void;
}

export function useZonePresetSelection({ siteId, mode, initialPresetId, selection, setSelection, onSave }: Options) {
  const { permissions } = useStores();
  const [presetId, setPresetId] = React.useState<string | null>(initialPresetId);
  const [isChoosing, setChoosing] = React.useState(false);

  const { data: presets, isSuccess: presetsLoaded } = useZonePresets(siteId);
  const { create, update } = useZonePresetMutations(siteId ?? 0);

  // Maintaining presets is a site admin's job. Following one is not, so the picker stays available
  // either way and only the two writes are gated - the same split the API makes. Without the site's
  // numeric id there is nothing to create against either: the API would fall back to the caller's
  // own site, which is the wrong one for anyone who administers several.
  const canEditPresets = permissions.has('can_edit_site') && typeof siteId === 'number';

  const currentPreset = presets?.find((preset) => preset.id === presetId);

  // A preset the site no longer has leaves its id behind on whoever followed it. Read as following
  // nothing once the presets are actually in, because an id that resolves to nothing silences both
  // save prompts below - one wants no preset, the other one it can name - and is written back on
  // every save. Only the dead name is dropped; the zones themselves are untouched.
  const followsMissingPreset = presetsLoaded && !!presetId && !currentPreset;
  const followedPresetId = followsMissingPreset ? null : presetId;
  const currentPresetZoneIds = React.useMemo(
    () => (currentPreset?.zones ?? []).map((zone) => zone.uuid),
    [currentPreset]
  );

  const hasDrifted = !!currentPreset && !sameZones(selection, currentPresetZoneIds);

  const onBlockList = mode === IndoorGeofencingMode.Block && !!followedPresetId;

  const couldBecomePreset =
    canEditPresets && !followedPresetId && mode !== IndoorGeofencingMode.Block && selection.length > 0;

  const choose = (value: string) => {
    if (value === CUSTOM) {
      // The zones stay as they are; the sender simply stops following the preset they came from.
      // Emptying the selection here would throw away the very list they want to adjust.
      setPresetId(null);
      return;
    }
    const picked = presets?.find((preset) => preset.id === value);
    if (!picked) return;
    setPresetId(picked.id);
    setSelection(picked.zones.map((zone) => zone.uuid));
  };

  // Moving the preset moves everyone following it, so it is only ever reached through the prompt
  // the save puts up - which names that consequence before offering it.
  const updatePreset = () => {
    if (!currentPreset) return;
    update.mutate(
      { presetId: currentPreset.id, zoneIds: selection },
      { onSuccess: () => onSave({ zoneIds: selection, zonePresetId: currentPreset.id }) }
    );
  };

  const createPreset = (name: string) => {
    create.mutate(
      { name, zoneIds: selection },
      { onSuccess: (preset) => onSave({ zoneIds: selection, zonePresetId: preset.id }) }
    );
  };

  const save = () => {
    if (onBlockList) return;
    // Whose zones these are is the carer's call, so it is asked here rather than resolved for them.
    if (hasDrifted || couldBecomePreset) {
      setChoosing(true);
      return;
    }
    onSave({ zoneIds: selection, zonePresetId: followedPresetId });
  };

  return {
    // Empty while the site has no presets, which is the signal to render no picker at all rather
    // than one holding only "zones of its own".
    options: presets?.length
      ? [
          { value: CUSTOM, label: t('geofence.preset.custom') },
          ...presets.map((preset) => ({
            value: preset.id,
            label:
              preset.id === presetId && hasDrifted ? t('geofence.preset.modified', { name: preset.name }) : preset.name,
          })),
        ]
      : [],
    value: followedPresetId ?? CUSTOM,
    choose,
    onBlockList,
    save,
    choice: isChoosing
      ? {
          presetName: currentPreset?.name ?? null,
          canEditPresets,
          isPending: create.isPending || update.isPending,
          onUpdatePreset: updatePreset,
          onSaveAsNew: createPreset,
          onKeepCustom: () => onSave({ zoneIds: selection, zonePresetId: null }),
          onCancel: () => setChoosing(false),
        }
      : null,
  };
}
